<template>
  <div :class="['sidebar', { 'is-collapsed': isCollapsed, 'is-peeking': isCollapsed && isPeeking }]"
       @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
    <div class="sidebar-header">
      <router-link to="/" class="brand">
        <img alt="Zerticarbon Logo" src="@/assets/logo.png" class="logo-img" v-if="showExpanded">
        <img alt="Zerticarbon Logo" src="@/assets/img/logo-white-icon.png" class="logo-img-small" v-else>
      </router-link>
    </div>

    <button class="toggle-btn" @click="toggleSidebar" aria-label="Toggle sidebar">
      <i class="fas fa-chevron-left" :class="{ 'is-flipped': isCollapsed }"></i>
    </button>

    <div class="sidebar-menu">
      <ul class="nav-list">
        <li class="nav-item">
          <router-link to="/areas-map" class="nav-link" data-title="Forests Map">
            <span class="nav-icon-wrap"><i class="fas fa-map-marked-alt nav-icon"></i></span>
            <span class="nav-text" v-if="showExpanded">Forests Map</span>
          </router-link>
        </li>

        <!-- Administrator Links -->
        <template v-if="isAdmin">
          <li class="nav-title" v-if="showExpanded">Administration</li>
          <li class="nav-title-divider" v-else></li>
          <li class="nav-item">
            <router-link to="/areas-list" class="nav-link" data-title="Forest List">
              <span class="nav-icon-wrap"><i class="fas fa-list-ul nav-icon"></i></span>
              <span class="nav-text" v-if="showExpanded">Forest List</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/statistics" class="nav-link" data-title="Statistics">
              <span class="nav-icon-wrap"><i class="fas fa-chart-line nav-icon"></i></span>
              <span class="nav-text" v-if="showExpanded">Statistics</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/simulator" class="nav-link" :class="{ 'router-link-active': $route.path === '/simulations' }" data-title="Simulator">
              <span class="nav-icon-wrap"><i class="fas fa-calculator nav-icon"></i></span>
              <span class="nav-text" v-if="showExpanded">Simulator</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/tree-types" class="nav-link" data-title="Tree Types">
              <span class="nav-icon-wrap"><i class="fas fa-seedling nav-icon"></i></span>
              <span class="nav-text" v-if="showExpanded">Tree Types</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/customers" class="nav-link" data-title="Customers">
              <span class="nav-icon-wrap"><i class="fas fa-users nav-icon"></i></span>
              <span class="nav-text" v-if="showExpanded">Customers</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/companies" class="nav-link" data-title="Companies">
              <span class="nav-icon-wrap"><i class="fas fa-building nav-icon"></i></span>
              <span class="nav-text" v-if="showExpanded">Companies</span>
            </router-link>
          </li>
        </template>

        <!-- Authenticated Regular User -->
        <template v-if="isAuthUser && !isAdmin">
          <li class="nav-item">
            <router-link to="/areas-list" class="nav-link" data-title="My Forests">
              <span class="nav-icon-wrap"><i class="fas fa-tree nav-icon"></i></span>
              <span class="nav-text" v-if="showExpanded">My Forests</span>
            </router-link>
          </li>
        </template>

        <li class="nav-item" v-if="isWeb2UserConnected">
          <router-link to="/retire-tokens" class="nav-link" data-title="Retire CO2">
            <span class="nav-icon-wrap"><i class="fas fa-leaf nav-icon"></i></span>
            <span class="nav-text" v-if="showExpanded">Retire CO2</span>
          </router-link>
        </li>

        <!-- Marketplace and My Orders hidden from the menu for now (the pages still exist) -->
        <li class="nav-item" v-if="isWeb2UserConnected" style="display: none">
          <router-link to="/marketplace" class="nav-link" data-title="Marketplace">
            <span class="nav-icon-wrap"><i class="fas fa-store nav-icon"></i></span>
            <span class="nav-text" v-if="showExpanded">Marketplace</span>
          </router-link>
        </li>

        <li class="nav-item" v-if="isWeb2UserConnected" style="display: none">
          <router-link to="/my-orders" class="nav-link" data-title="My Orders">
            <span class="nav-icon-wrap"><i class="fas fa-receipt nav-icon"></i></span>
            <span class="nav-text" v-if="showExpanded">My Orders</span>
          </router-link>
        </li>

        <li class="nav-item" v-if="isWeb2UserConnected">
          <router-link to="/notifications" class="nav-link" data-title="Notifications">
            <span class="nav-icon-wrap">
              <i class="fas fa-bell nav-icon"></i>
              <span v-if="unreadCount" class="nav-badge-dot"></span>
            </span>
            <span class="nav-text" v-if="showExpanded">
              Notifications
              <span v-if="unreadCount" class="nav-badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
            </span>
          </router-link>
        </li>

        <li class="nav-title" v-if="showExpanded && (config.exchange_url || config.liquidity_url)">External</li>
        <li class="nav-title-divider" v-else-if="config.exchange_url || config.liquidity_url"></li>

        <li class="nav-item" v-if="config && config.exchange_url">
          <a :href="config.exchange_url" target="_blank" class="nav-link" data-title="Exchange">
            <span class="nav-icon-wrap"><i class="fas fa-exchange-alt nav-icon"></i></span>
            <span class="nav-text" v-if="showExpanded">Exchange</span>
          </a>
        </li>
        <li class="nav-item" v-if="config && config.liquidity_url">
          <a :href="config.liquidity_url" target="_blank" class="nav-link" data-title="Liquidity">
            <span class="nav-icon-wrap"><i class="fas fa-water nav-icon"></i></span>
            <span class="nav-text" v-if="showExpanded">Liquidity</span>
          </a>
        </li>
      </ul>
    </div>

    <div class="sidebar-footer" v-if="isWeb2UserConnected && getWeb2ActiveAccount">
      <div class="user-mini-card" :data-title="userDisplayName">
        <div class="avatar">{{ userInitial }}</div>
        <div class="user-meta" v-if="showExpanded">
          <span class="user-email">{{ userDisplayName }}</span>
          <span class="user-role">{{ getWeb2ActiveAccount.role.name }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Config from "../../api/Config";
import Notification from "../../api/Notification";
import socket from "../../utils/socket";

export default {
  name: 'Sidebar',
  data() {
    return {
      isCollapsed: false,
      isPeeking: false,
      hoverTimer: null,
      config: {},
      unreadCount: 0,
      unsubscribeNotifications: null
    }
  },
  created() {
    Config.getConfig().then((config) => {
      this.config = config.data;
    }).catch(console.error);

    if (localStorage.getItem('sidebar-collapsed') === 'true') {
      this.isCollapsed = true;
    }

    if (window.innerWidth < 992) {
      this.isCollapsed = true;
    }

    // isWeb2UserConnected can still be false here on a fresh page load — the
    // session-restore-from-token action is async — so this is watched with
    // `immediate` rather than checked once, matching Header.vue/Notifications.vue.
    this.$watch('isWeb2UserConnected', (connected) => {
      if (connected) {
        this.refreshUnreadCount();
        if (this.unsubscribeNotifications) this.unsubscribeNotifications();
        this.unsubscribeNotifications = socket.onNotification(() => {
          this.unreadCount += 1;
        });
      } else {
        this.unreadCount = 0;
        if (this.unsubscribeNotifications) this.unsubscribeNotifications();
      }
    }, { immediate: true });

    this.$root.$on('zc-notifications:read', this.refreshUnreadCount);
  },
  computed: {
    ...mapGetters("user", ["isWeb2UserConnected", "getWeb2ActiveAccount"]),
    isAdmin() {
      return this.isWeb2UserConnected && this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role.name === 'Administrator';
    },
    isAuthUser() {
      return this.isWeb2UserConnected && this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role.name === 'Authenticated';
    },
    userDisplayName() {
      const account = this.getWeb2ActiveAccount;
      return (account && (account.firstName || account.username || account.email)) || '';
    },
    userInitial() {
      return this.userDisplayName ? this.userDisplayName.charAt(0).toUpperCase() : '?';
    },
    showExpanded() {
      return !this.isCollapsed || this.isPeeking;
    }
  },
  beforeDestroy() {
    clearTimeout(this.hoverTimer);
    if (this.unsubscribeNotifications) this.unsubscribeNotifications();
    this.$root.$off('zc-notifications:read', this.refreshUnreadCount);
  },
  methods: {
    refreshUnreadCount() {
      if (!this.isWeb2UserConnected) return;
      Notification.getUnreadCount()
        .then((res) => {
          this.unreadCount = res.data.count;
        }).catch(() => {});
    },
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      this.isPeeking = false;
      localStorage.setItem('sidebar-collapsed', this.isCollapsed);
      this.$emit('toggled', this.isCollapsed);
    },
    onMouseEnter() {
      if (!this.isCollapsed) return;
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => {
        this.isPeeking = true;
      }, 150);
    },
    onMouseLeave() {
      clearTimeout(this.hoverTimer);
      this.isPeeking = false;
    }
  }
}
</script>

<style scoped lang="scss">
.sidebar {
  width: 260px;
  min-height: 100vh;
  background: var(--sidebar-gradient);
  color: var(--sidebar-text);
  transition: width 0.32s cubic-bezier(0.22, 1, 0.36, 1);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1000;
  box-shadow: var(--shadow-lg);
  overflow: visible;

  // Below the "lg" breakpoint the unified mobile menu (in the Header) takes
  // over navigation entirely, so the rail is hidden rather than icon-only.
  @media (max-width: 991px) {
    display: none;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% -10%, rgba(var(--accent-green-rgb), 0.25) 0%, transparent 55%);
    pointer-events: none;
  }

  &.is-collapsed {
    width: 76px;

    &.is-peeking {
      width: 260px;
      box-shadow: var(--shadow-lg), 0 0 0 1px rgba(var(--accent-green-rgb), 0.25);
    }

    &:not(.is-peeking) {
      .nav-item .nav-link {
        justify-content: center;
        padding: 13px 0;
      }

      .nav-icon-wrap {
        margin-right: 0;
      }

      .user-mini-card {
        justify-content: center;
        padding: 10px 0;
      }
    }
  }
}

.sidebar-header {
  height: 72px;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background-color: var(--sidebar-header-bg);
  border-bottom: 1px solid var(--sidebar-border);
  position: relative;
  z-index: 1;

  .brand {
    display: flex;
    align-items: center;
    text-decoration: none;
  }

  .logo-img {
    height: 36px;
    max-width: 160px;
    object-fit: contain;
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35));
  }

  .logo-img-small {
    height: 30px;
    width: 30px;
    object-fit: contain;
    object-position: center;
  }
}

// Collapse handle: a small floating button on the sidebar's edge, always in
// the same spot whether the rail is expanded, collapsed or peeking open.
.toggle-btn {
  position: absolute;
  top: 23px;
  right: -13px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  color: var(--text-main);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  z-index: 2;
  box-shadow: var(--shadow-md);

  i {
    transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
  }

  i.is-flipped {
    transform: rotate(180deg);
  }

  &:hover {
    color: #fff;
    background: var(--accent-gradient);
    border-color: transparent;
    transform: scale(1.1);
  }
}

.sidebar-menu {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 12px;
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 999px;
  }
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-title {
  padding: 14px 12px 8px;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.35);
  font-weight: 700;
}

.nav-title-divider {
  height: 1px;
  background-color: var(--sidebar-border);
  margin: 14px 14px;
}

.nav-item {
  margin-bottom: 4px;
  animation: fadeInUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;

  .nav-link {
    position: relative;
    display: flex;
    align-items: center;
    padding: 11px 14px;
    color: var(--sidebar-text);
    text-decoration: none;
    transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
    border-radius: 10px;
    font-weight: 500;
    font-size: 0.92rem;

    &:hover {
      background-color: var(--sidebar-hover-bg);
      color: var(--sidebar-text-active);
      transform: translateX(2px);
    }

    &.router-link-exact-active {
      background: var(--sidebar-active-bg);
      color: var(--sidebar-text-active);
      font-weight: 600;

      &::before {
        content: '';
        position: absolute;
        left: -12px;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 22px;
        border-radius: 0 4px 4px 0;
        background: var(--accent-green-light);
        box-shadow: 0 0 10px rgba(var(--accent-green-rgb), 0.7);
      }

      .nav-icon-wrap {
        background: var(--accent-gradient);
        color: #fff;
        box-shadow: 0 4px 12px -2px var(--accent-glow);
      }
    }
  }

  .nav-icon-wrap {
    position: relative;
    width: 32px;
    height: 32px;
    min-width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .nav-link:hover .nav-icon-wrap {
    transform: scale(1.08) rotate(-4deg);
  }

  .nav-icon {
    font-size: 0.95rem;
    width: 1em;
    text-align: center;
  }

  .nav-text {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  // Shown next to the icon when the sidebar is collapsed (nav-text hidden)
  .nav-badge-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--accent-gradient);
    box-shadow: 0 0 0 2px var(--sidebar-header-bg, #0d1712);
  }

  // Shown next to the label when the sidebar is expanded
  .nav-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: var(--accent-gradient);
    color: #fff;
    font-size: 0.68rem;
    font-weight: 700;
  }
}

// staggered entrance for the first several items
.nav-item:nth-child(1) { animation-delay: 0.02s; }
.nav-item:nth-child(2) { animation-delay: 0.06s; }
.nav-item:nth-child(3) { animation-delay: 0.1s; }
.nav-item:nth-child(4) { animation-delay: 0.14s; }
.nav-item:nth-child(5) { animation-delay: 0.18s; }
.nav-item:nth-child(6) { animation-delay: 0.22s; }

// collapsed-state custom tooltip (hidden once the sidebar peeks open on hover)
.is-collapsed:not(.is-peeking) .nav-link[data-title]::after,
.is-collapsed:not(.is-peeking) .user-mini-card[data-title]::after {
  content: attr(data-title);
  position: absolute;
  left: calc(100% + 14px);
  top: 50%;
  transform: translateY(-50%) translateX(-6px);
  background: var(--bg-elevated);
  color: var(--text-main);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: var(--shadow-lg);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease, transform 0.18s ease;
  z-index: 2000;
}

.is-collapsed:not(.is-peeking) .nav-link[data-title]:hover::after,
.is-collapsed:not(.is-peeking) .user-mini-card[data-title]:hover::after {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

.sidebar-footer {
  border-top: 1px solid var(--sidebar-border);
  padding: 10px;
  position: relative;
  z-index: 1;
}

.user-mini-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-radius: 10px;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--sidebar-hover-bg);
  }

  .avatar {
    width: 34px;
    height: 34px;
    min-width: 34px;
    border-radius: 50%;
    background: var(--accent-gradient);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    box-shadow: var(--shadow-glow);
  }

  .user-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
  }

  .user-email {
    font-size: 0.8rem;
    color: var(--sidebar-text-active);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-role {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
}
</style>
